import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import {
  AdvancedFormReturnType,
  useAdvancedForm,
} from '../../../helpers/form/useAdvancedForm.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import clsx from 'clsx';
import styles from './SNLots.module.scss';
import { useWatch } from 'react-hook-form';
import { Filters } from './components/Filters.tsx';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import formStyles from '../../../components/form/form.module.scss';
import { DataGridRef, Pager, Scrolling } from 'devextreme-react/data-grid';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import { defineType, getColumnsConfig } from './components/helper/helper.tsx';
import { SerialNumberQuery } from 'services/api/api-client.ts';
import { AppPagination } from 'components/uikit/pagination/AppPagination.tsx';
import { TablePlaceholder } from 'components/uikit/table/TablePlaceholder.tsx';
import { sub } from 'date-fns';
import { useProcessLinks } from '../../../helpers/useProcessLinks.ts';
import { Tooltip } from '../../../components/uikit/Tooltip.tsx';
import { Trans, useTranslation } from 'react-i18next';
import { useAnalytics } from 'helpers/analytics.ts';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

export const SNLotsCategory = 'Serial Numbers & Lots';

export type FormParams = {
  typeOfSerialNumber: string;
  number: number | null;
  validFromAmountOfPeriods: number | null;
  validFromPeriodType: string;
};

export type PaginationProps = {
  page: number;
  perPage: number;
  totalCount: number;
  pageSizes: number[];
  changePagination: (pagination: { page?: number; perPage?: number }) => void;
};

export type SelectState = {
  typeOfSerialNumber: boolean;
  validFromLast: boolean;
};

export type FilterProps = {
  form: AdvancedFormReturnType<FormParams>;
  periodOptions: string[];
  typeOptions: string[];
  typeOfSerialNumber: string;
  number: number | null;
  validFromAmountOfPeriods: number | null;
  validFromPeriodType: string;
  isResetButtonShow: boolean;
  resetFilters: () => void;
  selectState: SelectState;
  changeSelectionState: (change: {
    [key in keyof SelectState]?: boolean;
  }) => void;
};

const pageSizes = [10, 20, 50];
const mail = 'supportHC@loewensteinmedical.com';

export const SNLots = () => {
  const i18n = useScopedTranslation('Page.SNLots');
  const i18nCommon = useTranslation();
  const { trackEvent } = useAnalytics();

  const [selectState, setSelectState] = useState<SelectState>({
    typeOfSerialNumber: false,
    validFromLast: false,
  });

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    isTooltipVisible,
    closeTooltip,
  } = useProcessLinks();

  const periodOptions = ['year', 'month', 'week', 'day'];

  const typeOptions = ['all', 'delivery_note', 'invoice', 'other'];

  useDataGrid();
  const [params, setParams] = useQueryParams({
    typeOfSerialNumber: StringParam,
    number: NumberParam,
    validFromAmountOfPeriods: NumberParam,
    validFromPeriodType: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'validFrom'),
    desc: withDefault(BooleanParam, true),
  });

  const form = useAdvancedForm<FormParams>(
    async (data) => {
      setParams({
        ...params,
        ...data,
        page: 1,
      });
    },
    {
      defaultValues: {
        typeOfSerialNumber: params.typeOfSerialNumber ?? typeOptions[0],
        number: params.number ?? null,
        validFromAmountOfPeriods: params.validFromAmountOfPeriods ?? 1,
        validFromPeriodType: params.validFromPeriodType ?? periodOptions[0],
        ...pagingSortingToBackendRequest(params),
      },
    },
  );

  const [
    typeOfSerialNumber,
    number,
    validFromAmountOfPeriods,
    validFromPeriodType,
  ] = useWatch({
    control: form.control,
    name: [
      'typeOfSerialNumber',
      'number',
      'validFromAmountOfPeriods',
      'validFromPeriodType',
    ],
  });

  const getValidFrom = useMemo(() => {
    if (validFromPeriodType && validFromAmountOfPeriods) {
      return sub(new Date(), {
        [validFromPeriodType + 's']: validFromAmountOfPeriods,
      });
    } else {
      return null;
    }
  }, [params.validFromPeriodType, params.validFromAmountOfPeriods]);

  const SNLotsQuery = SerialNumberQuery.useGetQuery(
    {
      number: params.number ? String(params.number) : undefined,
      type: defineType(params.typeOfSerialNumber),
      validFrom: getValidFrom,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const numberOfRows = SNLotsQuery.data?.countOfRecords ?? 0;

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const paginationProps: PaginationProps = useMemo(() => {
    return {
      page: params.page,
      perPage: params.perPage,
      totalCount: SNLotsQuery?.data?.countOfRecords ?? 0,
      pageSizes: pageSizes,
      changePagination: changePagination,
    };
  }, [params, SNLotsQuery?.data?.countOfRecords]);

  useEffect(() => {
    const closeFunc = () => {
      setSelectState({
        validFromLast: false,
        typeOfSerialNumber: false,
      });
    };

    document.body.onscroll = closeFunc;

    return () => {
      document.body.onscroll = null;
    };
  }, []);

  const resetFilters = () => {
    form.reset({
      typeOfSerialNumber: typeOptions[0],
      number: null,
      validFromAmountOfPeriods: 5,
      validFromPeriodType: periodOptions[0],
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShow =
    !!params.typeOfSerialNumber ||
    !!params.number ||
    (!!params.validFromAmountOfPeriods && params.validFromAmountOfPeriods != 5);

  const changeSelectionState = (change: {
    [key in keyof SelectState]?: boolean;
  }) => {
    setSelectState((prevState: SelectState) => ({
      ...prevState,
      ...change,
    }));
  };

  const filterProps: FilterProps = {
    form,
    selectState,
    changeSelectionState,
    periodOptions,
    typeOptions,
    isResetButtonShow,
    resetFilters,
    number,
    validFromAmountOfPeriods,
    validFromPeriodType,
    typeOfSerialNumber,
  };

  const columnsConfig = getColumnsConfig(
    i18n,
    i18nCommon,
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    trackEvent,
  );
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const errObj = SNLotsQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !SNLotsQuery.isLoading &&
    !SNLotsQuery.data?.records.length &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'sn-lots-container'}>
      <Breadcrumbs />
      <Loading loading={SNLotsQuery.isLoading || SNLotsQuery.isPlaceholderData}>
        <Filters {...filterProps} />
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={SNLotsQuery.data?.records}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
            )}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: numberOfRows }}
            noDataText={''}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x, index) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={`${x.dataField}.${index}`}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                      isSortingBlocked={index === columnsConfig.length - 1}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {isPlaceholderVisible && <TablePlaceholder testId={'placeholder'} />}
        {!!numberOfRows && <AppPagination {...paginationProps} />}
        {isTooltipVisible && (
          <Tooltip
            isError={true}
            closeTooltip={closeTooltip}
            bodyContent={
              <Trans
                i18nKey={'fileNotFound'}
                components={{
                  supportLink: <a href={`mailto:${mail}`} />,
                }}
              />
            }
            className={styles.tooltip}
            showCancelButton={true}
            iconClassName={styles.tooltipIcon}
          />
        )}
      </Loading>
    </div>
  );
};
